import IconButton from '@components/v2/ui/IconButton/IconButton';
import { Grid } from '@mui/material';
import { useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';

import { saveAs } from 'file-saver';
import { Download } from 'lucide-react';
import LangEnum from '../../lang/LangEnum';
import { IMedia, getMediaCover } from '../../lib/MediasLib';
import MediaRenderer from '../../modules/media/MediaRenderer';
import { useTranslation } from '../../modules/translation/TranslationContext';
import snackbarStore from '../../stores/Snackbar.store';
import { EMPTY_MEDIAS_PATH } from '../../utils/images.constants';
import useWidth from '../Hooks/useWidth';
import EmptyPlaceholder from '../Table/EmptyPlaceholder';
import GroupedCarousel, {
  CAROUSEL_GRID_SIZES,
  getItemsSizeBySelectedBreakpoint,
} from './GroupedCarousel';

const useStyles = makeStyles()((theme) => ({
  root: {
    position: 'relative',
    minHeight: 100,
  },
  downloadAction: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: theme.zIndex.tooltip,
  },
  medias: {
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2],
  },
}));

type EmphasisMediasCarouselProps = {
  medias: IMedia[];
};

type EmphasisMediaActions = {
  onDownload: (media: IMedia) => void;
};

const EmphasisMediasCarousel = ({ medias }: EmphasisMediasCarouselProps) => {
  const { classes } = useStyles();
  const { handleMessage } = snackbarStore;
  const { activeBreakpoint } = useWidth();

  const { translate } = useTranslation();

  const cardsLength: number = getItemsSizeBySelectedBreakpoint(
    CAROUSEL_GRID_SIZES,
    activeBreakpoint,
  );

  const renderMediaGroupStep = useCallback(
    (items: IMedia[], actions: EmphasisMediaActions) => (
      <Grid container spacing={1.5}>
        {items.map((item, index) => (
          <Grid key={`id_${item.id || index}-${item.position}-${index}`} size={CAROUSEL_GRID_SIZES}>
            <div className={classes.root}>
              {item?.downloadUrl && (
                <IconButton
                  icon={Download}
                  color="secondary"
                  className={classes.downloadAction}
                  ariaLabel={translate(LangEnum.GLOBAL_DOWNLOAD)}
                  onClick={() => actions.onDownload(item)}
                />
              )}
              <MediaRenderer src={getMediaCover(item)} type="IMAGE" className={classes.medias} />
            </div>
          </Grid>
        ))}
      </Grid>
    ),
    [classes, translate],
  );

  const onDownload = (media: IMedia) => {
    if (media.url) {
      saveAs(media.url, `${media?.type}-teceo-${new Date().getTime()}`);
    } else {
      handleMessage({
        type: 'warning',
        message: translate(LangEnum.SNACKBAR_UNABLE_TO_DOWNLOAD),
      });
    }
  };

  if (!medias || medias.length === 0) {
    return <EmptyPlaceholder src={EMPTY_MEDIAS_PATH} message="Nenhuma mídia em destaque ainda!" />;
  }

  return (
    <>
      <GroupedCarousel<IMedia>
        groupSize={cardsLength}
        items={medias}
        renderItemGroup={(items) => renderMediaGroupStep(items, { onDownload })}
      />
    </>
  );
};

export default EmphasisMediasCarousel;
